import { useEffect, useRef, useState } from "react";
import cx from "classnames";
import { v4 as uuidv4 } from "uuid";
import Quill from "quill";
import "quill/dist/quill.snow.css";
// @ts-ignore
import Countable from "countable";
import { ValidationResult } from "../../types/forms";
import ValidationWarning from "./ValidationWarning";
import HtmlEditorToolbarTag from "../../types/generic/HtmlEditorToolbarTag";
import { createPortal } from "react-dom";
import {
  buildEditorCtaBlotHtml,
  deserializeStorageHtmlForEditor,
  serializeEditorHtmlForStorage,
} from "../../helpers/htmlEditorCtaUtils";

interface ToolbarModule {
  container: HTMLElement;
}

// ── CTA Blot — registered once at module level ───────────────────────────────
// Using a separate blot className (ql-korero-cta-blot) to avoid collision with
// the toolbar button class (ql-korero-cta) that Quill auto-generates.
let ctaBlotRegistered = false;

function ensureCtaBlotRegistered() {
  if (ctaBlotRegistered) return;
  ctaBlotRegistered = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const BlockEmbed = Quill.import("blots/block/embed") as any;

  class KoreroCTABlot extends BlockEmbed {
    static blotName = "korero-cta";
    static tagName = "div";
    static className = "ql-korero-cta-blot";

    static create(value: { label: string }) {
      const node = super.create(value) as HTMLElement;
      const label = value?.label || "Take me to Korero";
      node.setAttribute("data-label", label);
      node.setAttribute("contenteditable", "false");

      const wrapper = document.createElement("div");
      wrapper.innerHTML = buildEditorCtaBlotHtml(label);
      const blot = wrapper.firstElementChild as HTMLElement | null;
      node.innerHTML = blot?.innerHTML ?? "";
      return node;
    }

    static value(node: HTMLElement) {
      return {
        label: node.getAttribute("data-label") || "Take me to Korero",
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Quill.register(KoreroCTABlot as any);
}

// ── Component ────────────────────────────────────────────────────────────────

interface HtmlEditorProps {
  toolbarMode: "SIMPLE" | "ADVANCED";
  currentValue: string | null;
  onValueChange?(newValue: string): void;
  onValueChangeWithWordCount?(newValue: string, wordCount: number): void;
  heightPx?: number;
  inputId?: string | undefined;
  className?: string;
  isReadOnly?: boolean;
  placeholder?: string | undefined;

  /** Providing the currentWordCount and maxWordCount will display a word counter underneath the editor */
  currentWordCount?: number;
  maxWordCount?: number;

  /** Whether or not to display the validation warnings */
  showValidationErrors?: boolean;
  /** If validation has been run, this is the validity plus any errors */
  validationResult?: ValidationResult | null;
  /** Custom tags to display in the toolbar dropdown */
  customTags?: HtmlEditorToolbarTag[] | undefined;

  allowLinkOption?: boolean;

  /**
   * If provided, adds a CTA button toolbar option that inserts a locked
   * "Take me to Korero" button anchored to the bottom of the email.
   * The user can edit the visible label text by clicking the button.
   */
  ctaButton?: { defaultLabel: string };

  /**
   * Match paragraph spacing/typography to the transactional email master shell
   * content cell so line breaks in the editor preview like the sent email.
   */
  matchEmailContentStyles?: boolean;
}

function HtmlEditor({
  toolbarMode,
  currentValue,
  onValueChange,
  onValueChangeWithWordCount,
  inputId = undefined,
  heightPx = 200,
  className = "",
  placeholder = undefined,
  isReadOnly = false,
  currentWordCount,
  maxWordCount,
  showValidationErrors = false,
  validationResult = null,
  customTags = undefined,
  allowLinkOption = false,
  ctaButton = undefined,
  matchEmailContentStyles = false,
}: HtmlEditorProps) {
  const [containerId] = useState("editor-" + uuidv4());
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [isSourceMode, setIsSourceMode] = useState(false);
  const quillRef = useRef<Quill | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // CTA edit popover
  const [showCtaEditPopover, setShowCtaEditPopover] = useState(false);
  const [ctaEditLabel, setCtaEditLabel] = useState("");
  const [ctaEditPosition, setCtaEditPosition] = useState({ top: 0, left: 0 });
  const ctaEditInputRef = useRef<HTMLInputElement | null>(null);
  // Re-entry guard: prevents text-change→repositionCTAAtEnd→text-change infinite loop
  const isRepositioningCTA = useRef(false);

  // Quill registers "text-change" once in mount effect; without refs, onValueChange would stay
  // stuck on the first render and merge message updates into a stale notification object.
  const onValueChangeRef = useRef(onValueChange);
  const onValueChangeWithWordCountRef = useRef(onValueChangeWithWordCount);
  const isSourceModeRef = useRef(isSourceMode);
  const showMaxCountRef = useRef(maxWordCount !== undefined);
  const ctaButtonRef = useRef(ctaButton);
  /** Tracks the last value emitted to the parent; skips Quill sync when prop matches (user typing). */
  const lastEmittedValueRef = useRef(currentValue ?? "");
  onValueChangeRef.current = onValueChange;
  onValueChangeWithWordCountRef.current = onValueChangeWithWordCount;
  isSourceModeRef.current = isSourceMode;
  showMaxCountRef.current = maxWordCount !== undefined;
  ctaButtonRef.current = ctaButton;

  const handleTextChangeRef = useRef<(quill: Quill) => void>(() => {});

  const getToolbarOptions = () => {
    let toolbarOptions: any[] = [];

    if (customTags && customTags.length > 0) {
      toolbarOptions.push(["custom-tags"]);
    }

    if (toolbarMode === "SIMPLE") {
      toolbarOptions = [
        ...toolbarOptions,
        ["bold", "italic", "underline"],
        [{ header: [1, 2, 3, false] }],
      ];
    } else {
      toolbarOptions = [
        ...toolbarOptions,
        ["bold", "italic", "underline", "strike"],
        [{ header: 1 }, { header: 2 }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ align: [] }],
      ];
    }

    if (allowLinkOption) {
      toolbarOptions.push(["link"]);
    }

    if (ctaButton) {
      toolbarOptions.push(["korero-cta"]);
    }

    toolbarOptions.push(["clean", "source"]);

    return toolbarOptions;
  };

  const setupCustomIcons = () => {
    const icons = Quill.import("ui/icons") as Record<string, string>;
    icons["custom-tags"] =
      '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M21.41 11.58l-9-9A2 2 0 0011 2H4a2 2 0 00-2 2v7a2 2 0 00.59 1.42l9 9A2 2 0 0013 22a2 2 0 001.41-.59l7-7A2 2 0 0022 13a2 2 0 00-.59-1.42zM5.5 7A1.5 1.5 0 117 5.5 1.5 1.5 0 015.5 7z"/></svg>';
    icons["source"] =
      '<svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/></svg>';
    if (ctaButton) {
      // Button-shape with right arrow: clearly communicates "insert a link button"
      icons["korero-cta"] =
        '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="5"/><line x1="8" y1="12" x2="14" y2="12"/><polyline points="11 9.5 14 12 11 14.5"/></svg>';
    }
  };

  const toggleSourceMode = () => {
    if (!quillRef.current) return;
    setIsSourceMode((prev) => !prev);
  };

  const publishHtmlValue = (rawHtml: string) => {
    const value = ctaButtonRef.current
      ? serializeEditorHtmlForStorage(rawHtml)
      : rawHtml;
    lastEmittedValueRef.current = value;
    onValueChangeRef.current?.(value);
  };

  const handleSourceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    if (quillRef.current) {
      quillRef.current.root.innerHTML = ctaButton
        ? deserializeStorageHtmlForEditor(newContent)
        : newContent;
    }
    publishHtmlValue(newContent);
  };

  const handleCustomTagClick = (quill: Quill) => {
    const toolbar = quill.getModule("toolbar") as ToolbarModule;
    const button = toolbar.container.querySelector(".ql-custom-tags");
    if (button) {
      button.setAttribute("title", "Insert merge variable");
      button.addEventListener("click", (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = button.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setDropdownPosition({
          top: rect.bottom + scrollTop,
          left: rect.left,
        });
        setShowTagDropdown((prev) => !prev);
      });
    }
  };

  const findCtaBlot = (quill: Quill) =>
    quill.root.querySelector(".ql-korero-cta-blot");

  const removeCtaFromEditor = (quill: Quill): boolean => {
    const existing = findCtaBlot(quill);
    if (!existing) {
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const blotInstance = (Quill as any).find(existing);
    if (blotInstance) {
      blotInstance.remove();
    }
    return true;
  };

  const syncCtaToolbarState = (quill: Quill) => {
    const toolbar = quill.getModule("toolbar") as ToolbarModule;
    const button = toolbar?.container?.querySelector(".ql-korero-cta");
    if (!button) {
      return;
    }
    const hasCta = !!findCtaBlot(quill);
    button.classList.toggle("ql-active", hasCta);
    button.setAttribute(
      "title",
      hasCta ? "Remove call-to-action button" : "Add call-to-action button",
    );
  };

  /** Insert CTA at the end (replacing any existing blot first). */
  const insertCtaAtEnd = (quill: Quill, label: string) => {
    removeCtaFromEditor(quill);
    const len = quill.getLength();
    quill.insertEmbed(Math.max(0, len - 1), "korero-cta", { label }, "api");
    syncCtaToolbarState(quill);
  };

  /** Toolbar control: add CTA if absent, remove if already present. */
  const toggleCtaAtEnd = (quill: Quill, label: string) => {
    if (removeCtaFromEditor(quill)) {
      syncCtaToolbarState(quill);
      publishHtmlValue(quill.root.innerHTML);
      return;
    }
    const len = quill.getLength();
    quill.insertEmbed(Math.max(0, len - 1), "korero-cta", { label }, "api");
    syncCtaToolbarState(quill);
  };

  handleTextChangeRef.current = (quill: Quill) => {
    const content = quill.root.innerHTML;
    if (!isSourceModeRef.current) {
      publishHtmlValue(content);
    }
    if (showMaxCountRef.current && onValueChangeWithWordCountRef.current) {
      // @ts-ignore
      Countable.count(quill.root, (counter) => {
        const latestWordCount = counter?.words ? (counter.words as number) : 0;
        onValueChangeWithWordCountRef.current?.(content, latestWordCount);
      });
    }
    const ctaBtn = ctaButtonRef.current;
    if (ctaBtn && !isRepositioningCTA.current) {
      const ctaEl = findCtaBlot(quill);

      if (ctaEl) {
        // Check whether any non-empty content has appeared after the CTA
        let next = ctaEl.nextElementSibling;
        while (next) {
          if (next.textContent?.trim() || next.querySelector("img")) {
            // Content snuck in after the CTA — re-pin it to the end
            isRepositioningCTA.current = true;
            setTimeout(() => {
              const q = quillRef.current;
              const btn = ctaButtonRef.current;
              if (q && btn) {
                insertCtaAtEnd(q, btn.defaultLabel);
              }
              isRepositioningCTA.current = false;
            }, 0);
            break;
          }
          next = next.nextElementSibling;
        }
      }

      syncCtaToolbarState(quill);
    }
  };

  const initializeQuill = () => {
    if (!editorRef.current || quillRef.current) return;

    // Must register the blot BEFORE creating the Quill instance
    if (ctaButton) {
      ensureCtaBlotRegistered();
    }

    setupCustomIcons();

    const quill = new Quill(editorRef.current, {
      theme: "snow",
      placeholder,
      readOnly: isReadOnly,
      modules: {
        toolbar: {
          container: getToolbarOptions(),
          handlers: {
            "custom-tags": function () {
              // Handled via direct DOM event listener below
            },
            "korero-cta": function () {
              if (!ctaButton) return;
              toggleCtaAtEnd(quill, ctaButton.defaultLabel);
            },
            source: toggleSourceMode,
          },
        },
      },
    });

    if (currentValue) {
      quill.setText("");
      const editorHtml = ctaButton
        ? deserializeStorageHtmlForEditor(currentValue)
        : currentValue;
      quill.clipboard.dangerouslyPasteHTML(0, editorHtml, "api");
    }

    lastEmittedValueRef.current = currentValue ?? "";

    if (ctaButton) {
      syncCtaToolbarState(quill);
    }

    quill.on("text-change", () => handleTextChangeRef.current(quill));

    // First line of defence: redirect the cursor if it lands after the CTA.
    // The CTA is always the last block, so the position at (getLength() - 1)
    // is the Quill trailing newline — i.e. past the CTA. Redirect to
    // (getLength() - 2) which is at/just before the CTA blot.
    if (ctaButton) {
      quill.on(
        "selection-change",
        (range: { index: number; length: number } | null, _oldRange: unknown, source: string) => {
          if (source === "api" || !range || range.length > 0) return;
          if (!quill.root.querySelector(".ql-korero-cta-blot")) return;
          const docLen = quill.getLength();
          if (range.index >= docLen - 1) {
            quill.setSelection(Math.max(0, docLen - 2), 0, "api");
          }
        },
      );
    }

    handleCustomTagClick(quill);

    quillRef.current = quill;
  };

  useEffect(() => {
    initializeQuill();
    return () => {
      quillRef.current = null;
    };
  }, []);

  // Sync when the parent sets content externally (e.g. AI apply) without a local edit.
  useEffect(() => {
    const quill = quillRef.current;
    if (!quill) {
      return;
    }

    const incoming = currentValue ?? "";
    if (incoming === lastEmittedValueRef.current) {
      return;
    }

    lastEmittedValueRef.current = incoming;
    quill.setText("");

    if (incoming) {
      const editorHtml = ctaButtonRef.current
        ? deserializeStorageHtmlForEditor(incoming)
        : incoming;
      quill.clipboard.dangerouslyPasteHTML(0, editorHtml, "api");
    }

    if (ctaButtonRef.current) {
      syncCtaToolbarState(quill);
    }
  }, [currentValue]);

  // Click-to-edit: detect clicks on the CTA blot inside the editor
  useEffect(() => {
    if (!ctaButton || !editorRef.current) return;
    const container = editorRef.current;

      const handleEditorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const ctaEl = target.closest(".ql-korero-cta-blot") as HTMLElement | null;
      if (ctaEl) {
        e.preventDefault();
        const label = ctaEl.getAttribute("data-label") || ctaButton.defaultLabel;
        // Use the visible button element's rect so the popover is centred over it
        const buttonEl = ctaEl.querySelector(".korero-cta-button") as HTMLElement | null;
        const anchorRect = (buttonEl ?? ctaEl).getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        const popoverWidth = 280;
        const estimatedPopoverHeight = 140;
        // Centre horizontally over the button, clamped within viewport edges
        const rawLeft = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
        const left = Math.max(8, Math.min(rawLeft, window.innerWidth - popoverWidth - 8));
        // Flip above when there isn't enough room below
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const top =
          spaceBelow < estimatedPopoverHeight + 16
            ? anchorRect.top + scrollY - estimatedPopoverHeight - 6
            : anchorRect.bottom + scrollY + 6;
        setCtaEditLabel(label);
        setCtaEditPosition({ top, left });
        setShowCtaEditPopover(true);
      }
    };

    container.addEventListener("click", handleEditorClick);
    return () => container.removeEventListener("click", handleEditorClick);
  }, [ctaButton]);

  // Auto-focus the popover input when it opens
  useEffect(() => {
    if (showCtaEditPopover) {
      setTimeout(() => ctaEditInputRef.current?.focus(), 50);
    }
  }, [showCtaEditPopover]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isClickInsideDropdown = dropdownRef.current?.contains(target);
      const isClickOnTagButton = target.closest(".ql-custom-tags");

      if (showTagDropdown && !isClickInsideDropdown && !isClickOnTagButton) {
        setShowTagDropdown(false);
      }
      if (
        showCtaEditPopover &&
        !target.closest(".cta-edit-popover") &&
        !target.closest(".ql-korero-cta-blot")
      ) {
        setShowCtaEditPopover(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showTagDropdown, showCtaEditPopover]);

  const insertTag = (tag: string) => {
    if (quillRef.current) {
      const range = quillRef.current.getSelection(true);
      if (range) {
        quillRef.current.insertText(range.index, tag);
        quillRef.current.setSelection(range.index + tag.length, 0);
      }
    }
    setTimeout(() => {
      setShowTagDropdown(false);
    }, 10);
  };

  /** Update the CTA blot's label in the editor DOM and fire onValueChange. */
  const handleCtaLabelSave = () => {
    if (!quillRef.current) return;
    const ctaEl = quillRef.current.root.querySelector(
      ".ql-korero-cta-blot",
    ) as HTMLElement | null;
    if (ctaEl) {
      const trimmed =
        ctaEditLabel.trim() || ctaButton?.defaultLabel || "Take me to Korero";
      ctaEl.setAttribute("data-label", trimmed);
      const labelSpan = ctaEl.querySelector(".korero-cta-label");
      if (labelSpan) labelSpan.textContent = trimmed;
      publishHtmlValue(quillRef.current.root.innerHTML);
    }
    setShowCtaEditPopover(false);
  };

  const showMaxCount = maxWordCount !== undefined;

  return (
    <div className="relative">
      {showValidationErrors && validationResult && (
        <ValidationWarning
          isValid={validationResult.isValid}
          errors={validationResult.errors}
        />
      )}

      <style type="text/css">
        {`
          #${containerId} {
            border-radius: 0.5rem;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            background-color: white;
          }

          #${containerId} .ql-toolbar {
            background-color: #f9fafb;
            border-top-left-radius: 0.5rem;
            border-top-right-radius: 0.5rem;
            border: 1px solid #d1d5db;
            border-bottom: 1px solid #d1d5db;
            padding: 0.5rem;
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            align-items: center;
          }

          #${containerId} .ql-formats {
            display: flex;
            gap: 0.25rem;
            align-items: center;
            margin-right: 0.75rem !important;
          }

          #${containerId} .ql-picker {
            height: 2rem;
          }

          #${containerId} .ql-picker-label {
            display: flex;
            align-items: center;
            padding: 0 0.5rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.375rem;
            height: 2rem;
            transition: all 150ms ease;
            background-color: white;
          }

          #${containerId} .ql-picker-label:hover {
            border-color: #d1d5db;
            background-color: #f9fafb;
          }

          #${containerId} .ql-picker-options {
            border-radius: 0.375rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            border: 1px solid #e5e7eb;
            padding: 0.25rem;
          }

          #${containerId} .ql-picker-item {
            padding: 0.375rem 0.75rem;
            border-radius: 0.25rem;
          }

          #${containerId} .ql-picker-item:hover {
            background-color: #f9fafb;
          }

          #${containerId} .ql-editor { 
            min-height: ${heightPx}px; 
            overflow-y: auto;
            display: ${isSourceMode ? "none" : "block"};
            padding: 1rem;
            border-bottom-left-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
            ${matchEmailContentStyles ? "font-size: 16px; line-height: 1.65;" : ""}
          }

          ${
            matchEmailContentStyles
              ? `
          #${containerId} .ql-editor p {
            margin: 0 0 16px 0;
          }

          #${containerId} .ql-editor p:last-child {
            margin-bottom: 0;
          }
          `
              : ""
          }

          #${containerId} .ql-container {
            border: 1px solid #d1d5db;
            border-top: none;
            border-bottom-left-radius: 0.5rem;
            border-bottom-right-radius: 0.5rem;
          }

          #${containerId} .ql-container:focus-within {
            border-color: #e5e7eb;
            outline: none;
          }

          #${containerId} button.ql-active,
          #${containerId} .ql-picker-label.ql-active {
            background-color: #f9fafb;
            border-color: #e5e7eb;
            color: #111827;
          }

          #${containerId} .ql-stroke {
            stroke: #6b7280;
          }

          #${containerId} button:hover .ql-stroke {
            stroke: #374151;
          }

          #${containerId} button:focus {
            outline: none;
          }

          #${containerId} button {
            width: 2rem;
            height: 2rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px solid #e5e7eb;
            border-radius: 0.375rem;
            transition: all 150ms ease;
            background-color: white;
          }

          #${containerId} button:hover {
            background-color: #f3f4f6;
            border-color: #d1d5db;
          }

          #${containerId} .source-textarea {
            display: ${isSourceMode ? "block" : "none"};
            width: 100%;
            min-height: ${heightPx}px;
            padding: 1rem;
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
            font-size: 0.875rem;
            line-height: 1.5;
            white-space: pre-wrap;
            border: 1px solid #e5e7eb;
            border-radius: 0.5rem;
            resize: vertical;
            background-color: white;
            transition: border-color 150ms ease;
          }

          #${containerId} .source-textarea:focus {
            outline: none;
            border-color: #d1d5db;
          }

          #${containerId} .ql-custom-tags svg,
          #${containerId} .ql-source svg,
          #${containerId} .ql-korero-cta svg {
            width: 1.25rem;
            height: 1.25rem;
          }

          /* ── CTA blot in editor ───────────────────────────────────────── */

          #${containerId} .ql-korero-cta-blot {
            display: block !important;
            margin-top: 12px;
            padding: 14px 0 10px;
            border-top: 1px dashed #d1d5db;
            text-align: center;
            cursor: pointer;
            user-select: none;
            -webkit-user-select: none;
          }

          #${containerId} .ql-korero-cta-blot:hover .korero-cta-button {
            opacity: 0.88;
          }

          #${containerId} .korero-cta-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 5px;
            pointer-events: none;
          }

          #${containerId} .korero-cta-button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 180px;
            color: #ffffff !important;
            padding: 11px 28px;
            border-radius: 8px;
            text-decoration: none !important;
            font-weight: 600;
            font-size: 0.875rem;
            line-height: 1;
            font-family: inherit;
            transition: opacity 150ms ease;
          }

          #${containerId} .korero-cta-hint {
            font-size: 0.7rem;
            color: #9ca3af;
            font-style: italic;
          }
        `}
      </style>

      <div id={containerId} className="relative flex flex-1 flex-col">
        <div ref={editorRef} className={cx(className, "flex-1 w-full")} />
        <textarea
          className="source-textarea"
          spellCheck={false}
          value={quillRef.current?.root.innerHTML || ""}
          onChange={handleSourceChange}
          placeholder="Enter HTML source code here..."
        />
      </div>

      {/* Variable tag dropdown */}
      {showTagDropdown &&
        customTags &&
        createPortal(
          <div
            ref={dropdownRef}
            className="tag-dropdown"
            style={{
              position: "fixed",
              maxHeight: "200px",
              overflowY: "auto",
              width: "200px",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 9999,
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              border: "1px solid #ccc",
              borderRadius: "4px",
              pointerEvents: "auto",
            }}
          >
            <div
              className="px-3 py-2 text-xs text-gray-500 border-b border-gray-200 bg-gray-50 pointer-events-none"
              role="presentation"
            >
              Insert merge variable at cursor
            </div>
            {customTags.map((tag, index) => (
              <button
                key={index}
                className="block w-full cursor-pointer px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                style={{ pointerEvents: "auto" }}
                onClick={() => {
                  insertTag(tag.tag);
                }}
              >
                {tag.name}
              </button>
            ))}
          </div>,
          document.body,
        )}

      {/* CTA label edit popover */}
      {showCtaEditPopover &&
        ctaButton &&
        createPortal(
          <div
            className="cta-edit-popover"
            style={{
              position: "absolute",
              top: ctaEditPosition.top,
              left: ctaEditPosition.left,
              zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "14px",
                width: "280px",
              }}
            >
              <p
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: "8px",
                }}
              >
                Edit button label
              </p>
              <input
                ref={ctaEditInputRef}
                type="text"
                value={ctaEditLabel}
                onChange={(e) => setCtaEditLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCtaLabelSave();
                  if (e.key === "Escape") setShowCtaEditPopover(false);
                }}
                style={{
                  width: "100%",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  fontSize: "0.875rem",
                  outline: "none",
                  marginBottom: "10px",
                  boxSizing: "border-box",
                }}
                placeholder="Enter button label…"
              />
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
              >
                <button
                  type="button"
                  onClick={() => setShowCtaEditPopover(false)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: "6px",
                    fontSize: "0.75rem",
                    border: "1px solid #e5e7eb",
                    backgroundColor: "white",
                    color: "#374151",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCtaLabelSave}
                  style={{
                    padding: "5px 12px",
                    borderRadius: "6px",
                    fontSize: "0.75rem",
                    backgroundColor: "#4f46e5",
                    color: "white",
                    border: "none",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {currentWordCount !== undefined && maxWordCount !== undefined && (
        <div className="text-right text-xs pt-2">
          <span
            className={cx(
              "inline-flex items-center gap-1 px-2 py-1 rounded-md",
              "bg-gray-50 border border-gray-200",
              "cursor-help transition-colors duration-150 hover:bg-gray-100",
              currentWordCount > maxWordCount
                ? "border-rose-200 bg-rose-50 hover:bg-rose-100"
                : ""
            )}
            title="Word count limit for this message"
            aria-label="Word count limit for this message"
          >
            <span
              className={cx(
                "font-medium",
                currentWordCount > maxWordCount
                  ? "text-rose-600"
                  : "text-gray-600"
              )}
            >
              {currentWordCount}
            </span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-500">{maxWordCount}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default HtmlEditor;
